import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { listPlaylists, createPlaylist, addTrackToPlaylist, Track, loadDb, fetchPlaylists } from "../../data/schema";
import LikeIcon from "../../../../assets/like.svg";
import { showToast } from "components/ui-kit/Toast";

type Props = {
  open: boolean;
  track: Track | null;
  selectedTrackIds?: Set<string>; // Optional: IDs of selected tracks for bulk operations
  onClose: () => void;
  onAdded?: () => void;
};

export const AddToPlaylistModal: React.FC<Props> = ({ open, track, selectedTrackIds, onClose, onAdded }) => {
  const [playlists, setPlaylists] = useState(listPlaylists());
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const rehydratePlaylists = useCallback(
    async (withLoader: boolean = false) => {
      setPlaylists(listPlaylists());
      if (withLoader) setIsLoading(true);
      try {
        const fresh = await fetchPlaylists();
        setPlaylists(fresh);
      } catch (error) {
        console.error("Unable to fetch playlists", error);
      } finally {
        if (withLoader) setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      setNewPlaylistName("");
      rehydratePlaylists(true);
    }
  }, [open, rehydratePlaylists]);

  // Ensure at least one playlist exists (Liked Songs)
  useEffect(() => {
    const db = loadDb();
    if (Object.keys(db.playlists).length === 0) {
      // This should be handled by loadDb, but just in case
      setPlaylists(listPlaylists());
    }
  }, []);

  const handlePlaylistClick = async (playlistId: string) => {
    if (!track) return;
    
    const playlist = playlists.find(p => p.id === playlistId);
    const playlistName = playlist?.name || "Playlist";
    
    // Get track IDs to add (either from selectedTrackIds or just the single track)
    const trackIdsToAdd = selectedTrackIds && selectedTrackIds.size > 0
      ? Array.from(selectedTrackIds)
      : [track.id];
    
    let addedCount = 0;
    let alreadyInPlaylistCount = 0;
    let errorCount = 0;
    
    // Закрываем модалку сразу после выбора плейлиста
    onClose();

    // Process tracks sequentially (дальше уже без UI-блокировки модалки)
    for (const trackId of trackIdsToAdd) {
      try {
        const success = await addTrackToPlaylist(playlistId, trackId);
        if (success) {
          addedCount++;
        } else {
          alreadyInPlaylistCount++;
        }
      } catch (error) {
        errorCount++;
        console.error(`Failed to add track ${trackId} to playlist:`, error);
      }
    }
    
    if (addedCount > 0) {
      showToast(
        addedCount === 1
          ? `Track added to ${playlistName}`
          : `${addedCount} tracks added to ${playlistName}`,
        undefined,
        'success'
      );
      await rehydratePlaylists();
      onAdded?.();
    }
    
    if (alreadyInPlaylistCount > 0) {
      showToast(
        `${alreadyInPlaylistCount} track(s) already in this playlist`,
        undefined,
        'error'
      );
    }
    
    if (errorCount > 0) {
      showToast(
        `${errorCount} track(s) failed to add`,
        undefined,
        'error'
      );
    }
  };

  const handleCreatePlaylist = async () => {
    if (!track || isCreating) return;
    
    const candidateName = newPlaylistName.trim();
    if (!candidateName) {
      showToast("Playlist name cannot be empty", undefined, "error");
      return;
    }

    setIsCreating(true);
    try {
      const playlist = await createPlaylist(newPlaylistName);

      const trackIdsToAdd =
        selectedTrackIds && selectedTrackIds.size > 0
      ? Array.from(selectedTrackIds)
      : [track.id];
    
    let addedCount = 0;
    let errorCount = 0;
    
    // Process tracks sequentially
    for (const trackId of trackIdsToAdd) {
      try {
        const success = await addTrackToPlaylist(playlist.id, trackId);
        if (success) {
          addedCount++;
        }
      } catch (error) {
        errorCount++;
        console.error(`Failed to add track ${trackId} to playlist:`, error);
      }
    }
    
    if (errorCount > 0) {
      showToast(
        `${errorCount} track(s) failed to add`,
        undefined,
        'error'
      );
    }
    
      showToast(`Playlist "${playlist.name}" created`, undefined, "success");
    setNewPlaylistName("");
      await rehydratePlaylists();
    onAdded?.();
    } catch (error) {
      console.error("Failed to create playlist", error);
      showToast("Unable to create playlist. Please try again.", undefined, "error");
    } finally {
      setIsCreating(false);
    }
  };

  if (!open) return null;

  const isLikedSongs = (playlistId: string) => playlistId === 'playlist_liked_songs';
  const likedSongsPlaylist = playlists.find(p => isLikedSongs(p.id));
  const otherPlaylists = playlists.filter(p => !isLikedSongs(p.id));

  const content = (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Add to Playlist</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        
        <PlaylistList>
          {likedSongsPlaylist && (
            <PlaylistItem onClick={() => handlePlaylistClick(likedSongsPlaylist.id)}>
              <PlaylistThumbnail>
                <ThumbnailImage>
                  <LikeIconWrapper>
                    <LikeIcon color="#fff" width="24px" height="24px" />
                  </LikeIconWrapper>
                </ThumbnailImage>
              </PlaylistThumbnail>
              <PlaylistName>{likedSongsPlaylist.name}</PlaylistName>
            </PlaylistItem>
          )}
          
          {otherPlaylists.map(playlist => (
            <PlaylistItem key={playlist.id} onClick={() => handlePlaylistClick(playlist.id)}>
              <PlaylistThumbnail>
                {playlist.coverImage ? (
                  <ThumbnailImageImg src={playlist.coverImage} alt={playlist.name} />
                ) : (
                  <ThumbnailImage $gradient={getGradientForPlaylist(playlist.id)} />
                )}
              </PlaylistThumbnail>
              <PlaylistName>{playlist.name}</PlaylistName>
            </PlaylistItem>
          ))}
        </PlaylistList>

        <CreateSection>
          <CreateInput
            placeholder="Playlist Name"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreatePlaylist();
              }
            }}
          />
          <CreateButton onClick={handleCreatePlaylist} disabled={isCreating}>
            {isCreating ? "Creating..." : "Create Playlist"}
          </CreateButton>
        </CreateSection>
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }
  return createPortal(content, document.body);
};

// Generate different gradients for playlists based on their ID
function getGradientForPlaylist(playlistId: string): string {
  const hash = playlistId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const gradients = [
    'linear-gradient(135deg, #ff9a9e, #fecfef)',
    'linear-gradient(135deg, #a1c4fd, #c2e9fb)',
    'linear-gradient(135deg, #ffecd2, #fcb69f)',
    'linear-gradient(135deg, #ff9a56, #ffad56)',
    'linear-gradient(135deg, #667eea, #764ba2)',
    'linear-gradient(135deg, #f093fb, #f5576c)',
    'linear-gradient(135deg, #4facfe, #00f2fe)',
    'linear-gradient(135deg, #43e97b, #38f9d7)',
  ];
  return gradients[hash % gradients.length];
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 44vw;
  max-width: calc(100% - 6vw);
  background: #fff;
  color: #000;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 2.2vh 2vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #000;
`;

const CloseButton = styled.button`
  width: 3.4vh;
  height: 3.4vh;
  border-radius: 1.7vh;
  border: none;
  background: #f4f4f4;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
  max-height: calc(5 * 80px); /* 5 items max, each ~80px tall */
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f4f4f4;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
    
    &:hover {
      background: #999;
    }
  }
`;

const PlaylistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
  }
`;

const PlaylistThumbnail = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ThumbnailImage = styled.div<{ $gradient?: string }>`
  width: 100%;
  height: 100%;
  background: ${p => p.$gradient || 'linear-gradient(135deg, #667eea, #764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LikeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistName = styled.div`
  font-size: 14px;
  color: #000;
  flex: 1;
`;

const CreateSection = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
`;

const CreateInput = styled.input`
  flex: 1;
  padding: 1.4vh 1.2vw;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  font-size: 14px;
  outline: none;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    border-color: #7987FF;
    background: #fff;
  }
`;

const CreateButton = styled.button`
  padding: 1.2vh 1.4vw;
  border-radius: 18px;
  border: none;
  background: #7987FF;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
  
  &:hover {
    background: #6b7cff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #7987FF;
  }
`;

const LoadingNote = styled.div`
  margin-top: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

export default AddToPlaylistModal;

